import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { 
  Box, 
  Typography, 
  Paper, 
  Button,
  CircularProgress,
  Alert,
  Stepper,
  Step,
  StepLabel
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

interface FileUploaderProps {
  onFileUpload: (file: File) => void;
  isUploading?: boolean;
  s3UploadStatus?: {
    isUploaded: boolean;
    error: string | null;
  };
  apiNotificationStatus?: {
    isNotified: boolean;
    error: string | null;
  };
}

const FileUploader: React.FC<FileUploaderProps> = ({ 
  onFileUpload, 
  isUploading = false,
  s3UploadStatus,
  apiNotificationStatus
}) => {
  const [error, setError] = useState<string | null>(null);
  const [uploadStep, setUploadStep] = useState(0);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) {
      return;
    }

    const file = acceptedFiles[0];
    
    // Check file type
    const validTypes = ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel', 'text/csv'];
    if (!validTypes.includes(file.type)) {
      setError('Please upload an Excel or CSV file');
      return;
    }

    // Check file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('File size exceeds 10MB limit');
      return;
    }

    setError(null);
    onFileUpload(file);
  }, [onFileUpload]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
      'text/csv': ['.csv']
    },
    multiple: false,
    disabled: isUploading
  });

  // Upload steps
  const uploadSteps = [
    'Select File',
    'Upload to Cloud',
    'Notify API',
    'Process Data'
  ];

  // Determine current step based on upload status
  React.useEffect(() => {
    if (isUploading) {
      if (s3UploadStatus?.isUploaded) {
        if (apiNotificationStatus?.isNotified) {
          setUploadStep(3); // Processing data
        } else {
          setUploadStep(2); // Notifying API
        }
      } else {
        setUploadStep(1); // Uploading to cloud
      }
    } else if (s3UploadStatus?.isUploaded && apiNotificationStatus?.isNotified) {
      setUploadStep(4); // Complete
    } else {
      setUploadStep(0); // Initial state
    }
  }, [isUploading, s3UploadStatus, apiNotificationStatus]);

  return (
    <Box sx={{ mt: 3, mb: 3 }}>
      {error && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 2, 
            borderRadius: 2,
            '& .MuiAlert-icon': {
              color: 'error.main'
            }
          }}
        >
          {error}
        </Alert>
      )}

      {s3UploadStatus?.error && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 2, 
            borderRadius: 2,
            '& .MuiAlert-icon': {
              color: 'error.main'
            }
          }}
        >
          {s3UploadStatus.error}
        </Alert>
      )}

      {apiNotificationStatus?.error && (
        <Alert 
          severity="warning" 
          sx={{ 
            mb: 2, 
            borderRadius: 2
          }}
        >
          API notification failed: {apiNotificationStatus.error}. Your file was uploaded but the system may not process it automatically.
        </Alert>
      )}

      {isUploading && (
        <Box sx={{ mb: 3 }}>
          <Stepper activeStep={uploadStep} alternativeLabel>
            {uploadSteps.map((label, index) => (
              <Step key={label} completed={index < uploadStep}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>
      )}

      <Paper
        {...getRootProps()}
        sx={{
          p: 4,
          border: '2px dashed',
          borderColor: isDragActive ? 'primary.main' : 'rgba(142, 44, 76, 0.2)',
          borderRadius: 3,
          bgcolor: isDragActive ? 'rgba(142, 44, 76, 0.04)' : 'background.paper',
          textAlign: 'center',
          cursor: isUploading ? 'default' : 'pointer',
          transition: 'all 0.2s ease',
          '&:hover': {
            borderColor: isUploading ? 'rgba(142, 44, 76, 0.2)' : 'primary.main',
            bgcolor: isUploading ? 'background.paper' : 'rgba(142, 44, 76, 0.04)'
          }
        }}
      >
        <input {...getInputProps()} disabled={isUploading} />
        
        {isUploading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <CircularProgress 
              size={50} 
              sx={{ 
                mb: 2,
                color: 'primary.main'
              }} 
            />
            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
              {uploadStep === 1 && 'Uploading file to cloud storage...'}
              {uploadStep === 2 && 'Notifying API about upload...'}
              {uploadStep === 3 && 'Processing data...'}
            </Typography>
          </Box>
        ) : s3UploadStatus?.isUploaded && apiNotificationStatus?.isNotified ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <CheckCircleOutlineIcon 
              sx={{ 
                fontSize: 50, 
                mb: 2,
                color: 'success.main'
              }} 
            />
            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
              File uploaded successfully and ready for processing!
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Box 
              sx={{ 
                width: 80, 
                height: 80, 
                borderRadius: '50%', 
                backgroundColor: 'rgba(142, 44, 76, 0.08)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 3
              }}
            >
              <FileUploadOutlinedIcon sx={{ fontSize: 40, color: 'primary.main' }} />
            </Box>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              {isDragActive ? 'Drop the file here' : 'Upload your spend data file'}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: '80%' }}>
              Upload your procurement data file (XLSX, XLS, CSV) to get started with spend analysis
            </Typography>
            <Button 
              variant="contained" 
              color="secondary"
              sx={{ 
                px: 3, 
                py: 1,
                borderRadius: 2,
                fontWeight: 500,
                boxShadow: 'none'
              }}
            >
              Browse Files
            </Button>
          </Box>
        )}
      </Paper>
      
      <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
        Supported formats: XLSX, XLS, CSV (max 10MB)
      </Typography>
    </Box>
  );
};

export default FileUploader; 